import { useMemo } from 'react'

import { SkeletonBox, TableFilters } from '@circlefin/components'
import useTranslation from 'next-translate/useTranslation'

import { PENDING_INFO_STATES } from '../../utils'

import type { FilterDropdownProps } from '@circlefin/components/lib/TableFilters/Dropdown/Dropdown'
import type {
  BlockchainAddressStatus,
  BlockchainRecipientAddressWithMetadata,
} from '@shared/graphql'

export interface StatusProps
  extends Omit<FilterDropdownProps, 'items' | 'placeholder' | 'name'> {
  /**
   * Unfiltered list of addresses.
   */
  addresses?: BlockchainRecipientAddressWithMetadata[]
  /**
   * Loading.
   */
  loading?: boolean
}

export const Status: React.FC<StatusProps> = ({
  addresses,
  loading = false,
  ...props
}) => {
  const { t } = useTranslation('address-book/filters')

  /**
   * We combine PENDING_ADDITIONAL_INFORMATION and PENDING_BENEFICIARY_INFORMATION into one option in the status filter.
   * If statusList already contains one status, we do not need to add the other.
   */
  const statusList = useMemo(() => {
    if (!addresses) return []

    return addresses.reduce<BlockchainAddressStatus[]>((acc, address) => {
      if (acc.includes(address.status)) return acc

      if (
        PENDING_INFO_STATES.includes(address.status) &&
        acc.some((status) => PENDING_INFO_STATES.includes(status))
      )
        return acc

      return [...acc, address.status]
    }, [])
  }, [addresses])

  const items = useMemo(() => {
    return [
      {
        value: undefined,
        label: t('table.status.all'),
      },
      ...statusList.map((status) => {
        return {
          label: t(`address-book/components:addressStatus.${status}`),
          value: status,
        }
      }),
    ]
  }, [statusList, t])

  return (
    <SkeletonBox className="w-full" loading={loading}>
      <TableFilters.Dropdown
        {...props}
        data-testid="status-filter"
        items={items}
        name="status"
        placeholder={t`table.status.label`}
      />
    </SkeletonBox>
  )
}
